import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/lib/data";

const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

export default function Hero() {
  const waUrl = `https://wa.me/${COMPANY.phoneRaw.replace("+", "")}?text=${encodeURIComponent(COMPANY.whatsappMsg)}`;

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center overflow-hidden pt-32 pb-20"
    >
      {/* Background image + gradient */}
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#050B1A] via-[#0A2160] to-[#0F3D8C]">
        <img
          src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2400&auto=format&fit=crop"
          alt="Students studying abroad"
          className="w-full h-full object-cover opacity-60"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#050B1A]/90 via-[#0A2160]/80 to-[#0F3D8C]/70" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(212,175,55,0.35),_transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_rgba(37,99,235,0.4),_transparent_50%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 w-full relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 text-white">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-xs tracking-[0.2em] uppercase"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#FCD34D]" />
              Hyderabad's #1 Overseas Education Consultants
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95]"
            >
              Study Abroad <br />
              with <span className="font-serif italic text-gradient-gold">Confidence</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-6 text-lg text-white/80 max-w-xl leading-relaxed"
            >
              Transform your dream into reality with {COMPANY.name}. Expert counselling, world-class training and end-to-end visa support — all under one roof.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Button
                data-testid="hero-consult-btn"
                onClick={() => scrollTo("#contact")}
                className="rounded-full bg-[#D4AF37] hover:bg-[#B99328] text-[#0F172A] font-semibold px-7 py-6 shadow-xl shadow-[#D4AF37]/30 group"
              >
                Book Free Consultation
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                data-testid="hero-apply-btn"
                onClick={() => scrollTo("#contact")}
                variant="outline"
                className="rounded-full bg-white/10 backdrop-blur-md border-white/30 text-white hover:bg-white/20 hover:text-white px-7 py-6"
              >
                Apply Now
              </Button>
              <a
                data-testid="hero-whatsapp-btn"
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold px-7 py-3.5 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp Us
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="mt-10 flex items-center gap-6 text-sm text-white/70"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#FCD34D]" />
                98% Visa Success Rate
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-7 h-7 rounded-full bg-gradient-to-br from-white/30 to-white/10 border-2 border-[#0F3D8C]" />
                  ))}
                </div>
                <span>1000+ Students Placed</span>
              </div>
            </motion.div>
          </div>

          {/* Floating stats card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="lg:col-span-5 hidden lg:block"
          >
            <div className="relative">
              <div className="glass rounded-3xl p-6 relative overflow-hidden animate-float">
                <img
                  src="https://images.unsplash.com/photo-1618355776464-8666794d2520?q=80&w=1200&auto=format&fit=crop"
                  alt="Graduation"
                  className="w-full aspect-[4/5] object-cover rounded-2xl"
                />
                <div className="absolute bottom-8 left-8 right-8 glass-solid rounded-2xl p-4 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <div className="text-2xl font-heading font-semibold text-[#0F172A] dark:text-white">1000+</div>
                    <div className="text-xs text-muted-foreground">Success Stories</div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-8 -right-6 glass rounded-2xl p-4 rotate-6 hidden xl:block">
                <div className="text-xs tracking-[0.2em] uppercase text-white/60">Next Batch</div>
                <div className="text-lg font-heading font-semibold text-white">IELTS · Dec 15</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-xs tracking-[0.3em] uppercase">
        Scroll to explore
      </div>
    </section>
  );
}
